import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { DocumentTemplate, TemplateField } from '../../core/app-store.js';
import { getPresetTemplate } from '../template-cache.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

// In-memory signature pads
const signatures: Record<string, string> = {};

export async function renderDocumentFiller(
  container: HTMLElement,
  navigate: NavigateFn,
  params: Record<string, string>
): Promise<void> {
  const { templateId } = params;
  if (!templateId) { navigate('templates'); return; }

  let template: DocumentTemplate | null = null;
  try {
    const api = getAPI();
    template = await api.getTemplate(templateId) as DocumentTemplate;
  } catch {
    template = getPresetTemplate(templateId);
  }

  if (!template) { navigate('templates'); return; }

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;align-items:center;gap:12px">
        <button class="btn btn-ghost btn-sm" id="back-btn">← Templates</button>
        <div>
          <h1 class="page-title" style="margin:0">${escHtml(template.name)}</h1>
          <p style="font-size:12px;color:var(--text-muted);margin-top:2px">${escHtml(template.category)} · ${template.fields.length} fields</p>
        </div>
      </div>
    </div>

    <div style="max-width:860px">
      <div class="card">
        <form id="doc-form">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px" id="form-grid">
            ${template.fields.map((f) => renderField(f)).join('')}
          </div>
          <div style="display:flex;gap:12px;justify-content:flex-end;margin-top:24px;padding-top:20px;border-top:1px solid var(--border)">
            <button type="button" class="btn btn-ghost" id="back-btn-2">Cancel</button>
            <button type="button" class="btn btn-primary" id="save-draft-btn">Save Draft</button>
            <button type="submit" class="btn btn-gold btn-lg" id="certify-btn">◈ Certify Document</button>
          </div>
        </form>
      </div>
    </div>
  `;

  document.getElementById('back-btn')!.addEventListener('click', () => navigate('templates'));
  document.getElementById('back-btn-2')!.addEventListener('click', () => navigate('templates'));

  // Init signature pads
  template.fields.filter((f) => f.type === 'signature').forEach((f) => {
    initSignaturePad(f.id);
  });

  document.getElementById('save-draft-btn')!.addEventListener('click', () => {
    saveDocument(template!, 'draft', navigate);
  });

  document.getElementById('doc-form')!.addEventListener('submit', (e) => {
    e.preventDefault();
    saveDocument(template!, 'certified', navigate);
  });
}

function renderField(f: TemplateField): string {
  const colSpan = f.width === 'half' ? '' : 'grid-column: 1 / -1;';

  if (f.type === 'signature') {
    return `
      <div style="${colSpan}">
        <label>${escHtml(f.label)}${f.required ? ' *' : ''}</label>
        <div class="sig-pad-wrap" id="sig-wrap-${f.id}">
          <canvas id="sig-${f.id}" class="sig-pad" width="400" height="120"></canvas>
          <div class="sig-pad-controls">
            <button type="button" class="btn btn-ghost btn-sm" data-clear="${f.id}">Clear</button>
            <span style="font-size:11px;color:var(--text-muted)">Draw your signature above</span>
          </div>
        </div>
      </div>
    `;
  }

  if (f.type === 'select') {
    return `
      <div style="${colSpan}">
        <label>${escHtml(f.label)}${f.required ? ' *' : ''}</label>
        <select id="field-${f.id}" ${f.required ? 'required' : ''}>
          <option value="">Select…</option>
          ${(f.options ?? []).map((o) => `<option value="${escHtml(o)}">${escHtml(o)}</option>`).join('')}
        </select>
      </div>
    `;
  }

  if (f.type === 'textarea') {
    return `
      <div style="${colSpan}">
        <label>${escHtml(f.label)}${f.required ? ' *' : ''}</label>
        <textarea id="field-${f.id}" placeholder="${escHtml(f.placeholder ?? '')}" ${f.required ? 'required' : ''} style="min-height:100px"></textarea>
      </div>
    `;
  }

  const inputType = f.type === 'currency' || f.type === 'number' ? 'number'
    : f.type === 'date' ? 'date' : 'text';

  return `
    <div style="${colSpan}">
      <label>${escHtml(f.label)}${f.required ? ' *' : ''}${f.type === 'currency' ? ' ($)' : ''}</label>
      <input type="${inputType}" id="field-${f.id}"
        placeholder="${escHtml(f.placeholder ?? f.defaultValue ?? '')}"
        ${f.required ? 'required' : ''}
        ${f.type === 'currency' ? 'min="0" step="0.01"' : ''}
        ${f.type === 'number' ? 'min="0"' : ''} />
    </div>
  `;
}

function initSignaturePad(fieldId: string): void {
  const canvas = document.getElementById(`sig-${fieldId}`) as HTMLCanvasElement | null;
  if (!canvas) return;

  const ctx = canvas.getContext('2d')!;
  ctx.strokeStyle = '#f1f5f9';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';

  let drawing = false;
  let lastX = 0, lastY = 0;

  const getPos = (e: MouseEvent | TouchEvent) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e instanceof TouchEvent) {
      const t = e.touches[0]!;
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const start = (e: MouseEvent | TouchEvent) => {
    drawing = true;
    const pos = getPos(e);
    [lastX, lastY] = [pos.x, pos.y];
    e.preventDefault();
  };

  const draw = (e: MouseEvent | TouchEvent) => {
    if (!drawing) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    [lastX, lastY] = [pos.x, pos.y];
    signatures[fieldId] = canvas.toDataURL();
    e.preventDefault();
  };

  const stop = () => { drawing = false; };

  canvas.addEventListener('mousedown', start);
  canvas.addEventListener('mousemove', draw);
  canvas.addEventListener('mouseup', stop);
  canvas.addEventListener('touchstart', start, { passive: false });
  canvas.addEventListener('touchmove', draw, { passive: false });
  canvas.addEventListener('touchend', stop);

  document.querySelector(`[data-clear="${fieldId}"]`)?.addEventListener('click', () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    delete signatures[fieldId];
  });
}

async function saveDocument(template: DocumentTemplate, mode: 'draft' | 'certified', navigate: NavigateFn): Promise<void> {
  const values: Record<string, string> = {};

  for (const f of template.fields) {
    if (f.type === 'signature') {
      values[f.id] = signatures[f.id] ?? '';
      if (f.required && !values[f.id]) {
        showToast(`Please sign: ${f.label}`, 'error');
        return;
      }
    } else {
      const el = document.getElementById(`field-${f.id}`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      values[f.id] = el?.value ?? '';
      if (f.required && !values[f.id]) {
        showToast(`Required: ${f.label}`, 'error');
        el?.focus();
        return;
      }
    }
  }

  const session = getSession();
  const payload = {
    templateId: template.id,
    businessId: session?.businessId ?? 'preview',
    title: `${template.name} — ${new Date().toLocaleDateString()}`,
    values,
    status: mode,
  };

  try {
    const api = getAPI();
    await api.createDocument(payload);
    showToast(mode === 'certified' ? '◈ Document certified' : 'Draft saved', 'success');
    navigate('templates');
  } catch {
    showToast(mode === 'certified' ? '◈ Document certified (preview)' : 'Draft saved (preview)', 'success');
    navigate('templates');
  }
}


function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
